/**
 * ConsultantProposalsEntry — a consultant's proposal (or several quotes for one discipline) for the
 * owner to approve (docs/TEAM.md "Proposals that file themselves", Matt 2026-11-02).
 *
 * The owner hires the consultant directly, so the card shows the consultant's own PDF and nothing of
 * ours: no summary, no recommendation (they can ask us if they want to talk it through). Approving one
 * asks for the signed copy: the Hub hosts no signature because every consultant's signature block is
 * different, so the owner signs the consultant's document and uploads it here (we send it on in their
 * name, with them copied), or says they sent it directly.
 *
 * entry.consultantProposals: { service, chosenId, proposals: [{ id, firm, fileUrl, fileName, receivedOn,
 *   decision, decidedOn, decidedBy, signed, signedVia, signedFileName }] }
 */

import { useRef, useState } from 'react'
import { J, PrimaryButton, GhostButton, DoneChip, ViewOnlyNote, fmtJourneyDate } from '../journeyUI.jsx'
import JourneyBook from './JourneyBook.jsx'
import PdfPageViewer from '../../components/PdfPageViewer.jsx'
import { consultantProposalAct, uploadConsultantSignedCopy } from '../../services/journey.js'

const S = {
  card: { padding: '20px 0 22px', borderTop: '0.5px solid rgba(7,39,45,0.1)' },
  firm: { fontFamily: '"DM Sans", sans-serif', fontSize: 16, fontWeight: 600, color: '#07272D' },
  small: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, lineHeight: 1.6, color: '#3a5260' },
  err: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#c0392b', marginTop: 10 },
  input: { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans", sans-serif', fontSize: 14, padding: '9px 12px', border: '0.5px solid rgba(7,39,45,0.25)', borderRadius: 4, color: '#07272D', background: '#fff', marginTop: 8 },
}

const ERRORS = {
  already_chose: 'You already chose another proposal for this. Please contact us to change it.',
  approve_first: 'Approve the proposal first.',
}

function downloadHref(url, name) {
  if (!url) return null
  return url + (url.includes('?') ? '&' : '?') + 'download=' + encodeURIComponent(name || 'proposal.pdf')
}

export default function ConsultantProposalsEntry({ entry, viewOnly, token }) {
  const d = entry.consultantProposals || { proposals: [] }
  const [proposals, setProposals] = useState(d.proposals || [])
  const [chosenId, setChosenId] = useState(d.chosenId || null)
  const [open, setOpen] = useState(null)        // proposal being read
  const [passing, setPassing] = useState(null)  // proposal id whose "not this one" note is open
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const fileRef = useRef(null)
  const service = (d.service || 'consultant').toLowerCase()
  const several = proposals.length > 1
  const chosen = proposals.find(p => p.id === chosenId) || null

  function patch(id, fields) { setProposals(ps => ps.map(p => p.id === id ? { ...p, ...fields } : p)) }
  async function run(fn) {
    setError(null); setBusy(true)
    try { await fn() } catch (e) { setError(ERRORS[e.code] || e.message || 'Something went wrong. Please try again.') }
    setBusy(false)
  }
  const approve = (p) => run(async () => {
    await consultantProposalAct(token, entry.id, { action: 'approve', proposalId: p.id })
    patch(p.id, { decision: 'approved', decidedOn: new Date().toISOString().slice(0, 10) }); setChosenId(p.id)
  })
  const decline = (p) => run(async () => {
    await consultantProposalAct(token, entry.id, { action: 'decline', proposalId: p.id, note: note.trim() || undefined })
    patch(p.id, { decision: 'declined' }); setPassing(null); setNote('')
  })
  const upload = (file) => run(async () => {
    if (!file) return
    if (!/^(application\/pdf|image\/(jpeg|png|heic|heif|webp))$/i.test(file.type || '') && !/\.(pdf|jpe?g|png|heic|heif|webp)$/i.test(file.name || '')) throw new Error('Please upload the signed copy as a PDF or a photo of the signed page.')
    await uploadConsultantSignedCopy(token, entry.id, chosen.id, file)
    patch(chosen.id, { signed: true, signedVia: 'upload', signedFileName: file.name })
  })
  const sentDirect = () => run(async () => {
    await consultantProposalAct(token, entry.id, { action: 'signed', proposalId: chosen.id, via: 'direct' })
    patch(chosen.id, { signed: true, signedVia: 'direct' })
  })

  return (
    <div>
      <div style={J.eyebrow}>For your approval</div>
      <h2 style={J.title}>{entry.title || 'Consultant proposal'}</h2>
      <hr style={J.rule} />
      <div style={J.body}>
        <p style={{ margin: 0 }}>
          {several
            ? `We asked ${proposals.length} firms to propose as the ${service} for your project. Their proposals are below, as they sent them. Choose the one you would like to hire.`
            : `${(proposals[0] && proposals[0].firm) || 'The consultant'} sent a proposal to be the ${service} on your project. It is below, as they sent it.`}
        </p>
        <p style={{ margin: '14px 0 0' }}>
          You hire them directly, so the agreement is between you and them. If you would like to talk it through first, call or email us.
        </p>
      </div>

      {proposals.map(p => {
        const isChosen = p.id === chosenId
        const other = chosenId && !isChosen
        return (
          <div key={p.id} style={S.card}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={S.firm}>{p.firm}</span>
              {p.receivedOn && <span style={J.mono}>Received {fmtJourneyDate(p.receivedOn)}</span>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 6px' }}>
              <JourneyBook label="Proposal" onOpen={() => setOpen(p)} downloadUrl={downloadHref(p.fileUrl, p.fileName)} />
            </div>

            {p.decision === 'declined' && <div style={{ marginTop: 10 }}><span style={J.mono}>You passed on this one</span></div>}
            {isChosen && <div style={{ marginTop: 10 }}><DoneChip>{p.signed ? 'Approved and signed' : 'Approved'}</DoneChip></div>}

            {viewOnly && !p.decision && !chosenId && <div style={{ marginTop: 12 }}><ViewOnlyNote /></div>}

            {!viewOnly && !p.decision && !other && !chosenId && (
              passing === p.id ? (
                <div style={{ marginTop: 12 }}>
                  <div style={S.small}>Anything you would like us to know? (optional)</div>
                  <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} style={S.input} aria-label="Note" />
                  <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                    <PrimaryButton onClick={() => decline(p)} disabled={busy}>Pass on {p.firm}</PrimaryButton>
                    <GhostButton onClick={() => { setPassing(null); setNote('') }} disabled={busy}>Back</GhostButton>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                  <PrimaryButton onClick={() => approve(p)} disabled={busy}>Approve {several ? p.firm : 'this proposal'}</PrimaryButton>
                  <GhostButton onClick={() => setPassing(p.id)} disabled={busy}>Not this one</GhostButton>
                </div>
              )
            )}
          </div>
        )
      })}

      {chosen && !chosen.signed && !viewOnly && (
        <div style={{ ...S.card, paddingTop: 22 }}>
          <div style={S.firm}>Your signed copy</div>
          <p style={{ ...S.small, marginTop: 8 }}>
            Sign {chosen.firm}{'’'}s proposal where they ask for your signature, then upload your signed copy here: a PDF, or a photo of the signed page from your phone. We send it to {chosen.firm} for you, with you copied.
          </p>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf,image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; upload(f) }} />
          <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            <PrimaryButton onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>{busy ? 'Uploading…' : 'Upload signed copy'}</PrimaryButton>
            <GhostButton onClick={sentDirect} disabled={busy}>I sent it to them directly</GhostButton>
          </div>
        </div>
      )}
      {chosen && chosen.signed && (
        <p style={{ ...S.small, marginTop: 14 }}>
          {chosen.signedVia === 'upload' ? `Thank you. We sent your signed copy to ${chosen.firm.replace(/\.$/, '')}.` : `Thank you. ${chosen.firm} has your signed copy.`} We will coordinate their work from here.
        </p>
      )}
      {chosen && !chosen.signed && viewOnly && <div style={{ marginTop: 12 }}><ViewOnlyNote>The owner signs and uploads this one.</ViewOnlyNote></div>}

      {error && <div style={S.err}>{error}</div>}
      {open && <PdfPageViewer pdfUrl={open.fileUrl} title={open.firm + ' proposal'} downloadUrl={downloadHref(open.fileUrl, open.fileName)} onClose={() => setOpen(null)} />}
    </div>
  )
}
